import { Injectable } from '@angular/core';
import {
  ClientClinicalTimelineEntryDto, ClientClinicalTimelineEntryTypeDto,
  ClientConsultationAttachmentDto, ClientConsultationDetailDto, ClientConsultationDischargeDto,
  ClientConsultationLaboratoryAnalysisDto, ClientConsultationMedicineDto, ClientConsultationNoteDto,
  ClientConsultationOverviewDto, ClientConsultationSectionDtoMap, ClientConsultationSummaryDto,
  ClientConsultationSupplyDto, ClientDiagnosisDto,
  ClientPatientPrescriptionsDto, ClientPrescriptionDto, ClientPrescriptionItemDto,
  ClientTreatmentDto, ClientTreatmentPlanDto, ClientVitalsSnapshotDto,
} from '../dtos/clinical-history.dto';
import {
  ClientClinicalTimelineEntryType, ClientConsultationAttachmentFile, ClientConsultationDetail,
  ClientConsultationDetailDiagnosis,
  ClientConsultationDetailLaboratoryAnalysis, ClientConsultationDetailMedicine,
  ClientConsultationDetailSupply, ClientConsultationDetailTreatment,
  ClientConsultationDetailTreatmentPlan, ClientConsultationDischarge, ClientConsultationNote,
  ClientConsultationOverview, ClientConsultationSectionDataMap, ClientConsultationSectionKey,
  ClientConsultationSummary, ClientDeworming, ClientDiagnosis,
  ClientPatientPrescriptions,
  ClientPrescription, ClientPrescriptionItem, ClientTimelineEntry, ClientTreatment,
  ClientVaccination, ClientVitalsSnapshot,
} from '../../domain/models/clinical-history.model';

// Twin of patients' `ConsultationSectionMappers` (see that file's own doc-comment): indexing this
// table with the section literal is what keeps `toConsultationSection` typed without a cast.
type ClientConsultationSectionMappers = {
  [K in ClientConsultationSectionKey]:
  (dto: ClientConsultationSectionDtoMap[K]) => ClientConsultationSectionDataMap[K];
};

const TREATMENT_PLAN_ITEM_KINDS = ['medicine', 'food', 'procedure', 'supply'] as const;

function isTreatmentPlanItemKind(
  value: string | null,
): value is ClientConsultationDetailTreatmentPlan['itemKind'] & string {
  return value !== null && (TREATMENT_PLAN_ITEM_KINDS as readonly string[]).includes(value);
}

// Same int-enum-to-Spanish-literal translation as `features/patients/data/mappers
// /clinical-history.mapper.ts`'s `ClinicalHistoryMapper.toTimelineEntry` -- duplicated rather than
// reused cross-feature (see `clinical-history.dto.ts` in this same feature for why), but the
// mapping itself is NOT reimplemented from scratch: it is the same lookup table, kept in sync with
// the shared backend enum `PatientClinicalTimelineEntryType`.
const TIMELINE_TYPE_MAP: Record<ClientClinicalTimelineEntryTypeDto, ClientClinicalTimelineEntryType> = {
  [ClientClinicalTimelineEntryTypeDto.Consultation]: 'consulta',
  [ClientClinicalTimelineEntryTypeDto.Diagnosis]: 'diagnostico',
  [ClientClinicalTimelineEntryTypeDto.Vaccination]: 'vacuna',
  [ClientClinicalTimelineEntryTypeDto.Treatment]: 'tratamiento',
  [ClientClinicalTimelineEntryTypeDto.Deworming]: 'desparasitacion',
};

// Twin of `features/patients`' `toConsultationNotes`/`extractRawNoteEntries` (duplicated rather than
// imported cross-feature, see `clinical-history.dto.ts` in this same feature for why): `Notes` wins
// whenever the response carries it -- an empty array included, since that is an authoritative "no
// note with text" -- and the raw rows are only parsed when the key is absent altogether, i.e.
// against a backend deployed before the typed projection existed. That legacy path resolves neither
// author nor type, so both stay null; it disappears together with `NotesRaw`.
function toConsultationNotes(dto: ClientConsultationDetailDto): readonly ClientConsultationNote[] {
  if (dto.Notes) return dto.Notes.map(note => toNoteEntry(note));

  return extractRawNoteEntries(dto.NotesRaw ?? []);
}

function toNoteEntry(dto: ClientConsultationNoteDto): ClientConsultationNote {
  return {
    text: dto.Text,
    date: dto.DateAdded ?? null,
    typeLabel: dto.TypeLabel ?? null,
    authorName: dto.AuthorName ?? null,
  };
}

function toAttachmentFile(dto: ClientConsultationAttachmentDto): ClientConsultationAttachmentFile {
  return {
    noteId: dto.NoteId,
    uploadId: dto.UploadId,
    fileName: dto.FileName,
    sizeBytes: dto.SizeBytes,
    contentType: dto.ContentType,
  };
}

// A raw `consultationnote` row has no confirmed schema, so only surface an entry when one of these
// plausible keys resolves to a non-empty string -- never dump the raw JSON object into the UI. Same
// filter the backend's own `BuildConsultationNotes` applies, so both paths keep and drop the same rows.
function extractRawNoteEntries(raw: readonly Record<string, unknown>[]): ClientConsultationNote[] {
  const entries: ClientConsultationNote[] = [];

  for (const entry of raw) {
    for (const key of ['text', 'note']) {
      const value = entry[key];
      if (typeof value === 'string' && value.trim().length > 0) {
        const dateAdded = entry['date_added'];
        entries.push({
          text: value,
          date: typeof dateAdded === 'string' && dateAdded.trim().length > 0 ? dateAdded : null,
          typeLabel: null,
          authorName: null,
        });
        break;
      }
    }
  }

  return entries;
}

@Injectable()
export class ClientClinicalHistoryMapper {
  // Patient identity now travels ON the entry (`PatientId`/`PatientName`), not on a bundle wrapper:
  // the client-scoped timeline is a single flat, cross-patient array.
  public toTimelineEntry(dto: ClientClinicalTimelineEntryDto): ClientTimelineEntry {
    const type = TIMELINE_TYPE_MAP[dto.Type];

    return {
      id: dto.Id,
      patientId: dto.PatientId ?? null,
      patientName: dto.PatientName ?? null,
      type,
      title: dto.Title,
      date: dto.Date ?? null,
      description: dto.Description ?? null,
      sourceId: dto.SourceId,
      veterinarianName: dto.VeterinarianName ?? null,
      departmentName: dto.DepartmentName ?? null,
      statusCode: dto.StatusCode ?? null,
      statusLabel: dto.StatusLabel ?? null,
      invoice: dto.InvoiceId === null ? null : {
        total: dto.InvoiceTotal ?? null,
        // Prefer the real boolean when the backend sends it; only fall back to string-matching
        // the (backend-hardcoded) InvoicePaymentStatus for older/incomplete payloads.
        isPending: dto.InvoiceIsPending ?? (dto.InvoicePaymentStatus === 'pending'),
      },
      report: dto.NoteId === null ? null : {
        id: dto.NoteId,
        title: dto.NoteTitle ?? null,
      },
      attachmentNotes: (dto.AttachmentNotes ?? []).map((note) => ({
        noteId: note.NoteId,
        title: note.Title ?? null,
        typeLabel: note.TypeLabel ?? null,
        date: note.Date ?? null,
      })),
      consultationId: dto.ConsultationId ?? null,
      diagnosis: type === 'diagnostico' ? this.toEntryDiagnosis(dto) : null,
      vaccination: type === 'vacuna' ? this.toEntryVaccination(dto) : null,
      treatment: type === 'tratamiento' ? this.toEntryTreatment(dto) : null,
      deworming: type === 'desparasitacion' ? this.toEntryDeworming(dto) : null,
    };
  }

  public toVitalsSnapshot(dto: ClientVitalsSnapshotDto): ClientVitalsSnapshot {
    return {
      text: dto.Text ?? null,
      weight: dto.Weight ?? null,
      weightTimestamp: dto.WeightTimestamp ?? null,
      weightUnits: dto.WeightUnits ?? null,
      temperature: dto.Temperature ?? null,
      temperatureTimestamp: dto.TemperatureTimestamp ?? null,
      temperatureUnits: dto.TemperatureUnits ?? null,
      heartRate: dto.HeartRate ?? null,
      heartRateTimestamp: dto.HeartRateTimestamp ?? null,
      respiratoryRate: dto.RespiratoryRate ?? null,
      respiratoryRateTimestamp: dto.RespiratoryRateTimestamp ?? null,
      bodyConditionScore: dto.BodyConditionScore ?? null,
      bodyConditionScoreTimestamp: dto.BodyConditionScoreTimestamp ?? null,
      bloodPressure: dto.BloodPressure ?? null,
      bloodPressureTimestamp: dto.BloodPressureTimestamp ?? null,
      bloodPressureBodyPositionLabel: dto.BloodPressureBodyPositionLabel ?? null,
      bloodPressureBodyPositionCode: dto.BloodPressureBodyPositionCode ?? null,
      peripheralOxygenSaturation: dto.PeripheralOxygenSaturation ?? null,
      peripheralOxygenSaturationTimestamp: dto.PeripheralOxygenSaturationTimestamp ?? null,
      bloodGlucose: dto.BloodGlucose ?? null,
      centralVenousPressure: dto.CentralVenousPressure ?? null,
    };
  }

  // Nested diagnoses/items are untagged (`ClientConsultationDetailDiagnosis`/
  // `ClientConsultationDetailMedicine`) -- see those types' doc-comments in `clinical-history.model.ts`
  // for why this on-demand, single-consultation payload doesn't need the cross-patient tag.
  public toConsultationDetail(dto: ClientConsultationDetailDto): ClientConsultationDetail {
    return {
      id: dto.ExternalId,
      complaint: dto.Complaint ?? null,
      admittedTime: dto.AdmittedTime ?? null,
      started: dto.Started ?? null,
      finished: dto.Finished ?? null,
      ward: dto.Ward ?? null,
      patientLocation: dto.PatientLocation ?? null,
      triage: dto.Triage ?? null,
      holdingPlaceNumber: dto.HoldingPlaceNumber ?? null,
      referringOrganization: dto.ReferringOrganization ?? null,
      referringVeterinarian: dto.ReferringVeterinarian ?? null,
      integrationStatus: dto.IntegrationStatus ?? null,
      externalSent: dto.ExternalSent ?? null,
      reportToAuthorities: dto.ReportToAuthorities ?? null,
      vitals: dto.Vitals ? this.toVitalsSnapshot(dto.Vitals) : null,
      dischargeInstructionsText: dto.DischargeInstructionsText ?? null,
      dischargeInstructionsPrintedAt: dto.DischargeInstructionsPrintedAt ?? null,
      notes: toConsultationNotes(dto),
      diagnoses: dto.Diagnoses.map(item => this.toConsultationDetailDiagnosis(item)),
      items: dto.Items.map(item => this.toConsultationDetailMedicine(item)),
      prescriptions: (dto.Prescriptions ?? []).map(item => this.toPrescription(item)),
      attachmentFiles: (dto.Attachments ?? []).map(attachment => toAttachmentFile(attachment)),
    };
  }

  public toConsultationSummary(dto: ClientConsultationSummaryDto): ClientConsultationSummary {
    return {
      id: dto.ExternalId,
      title: dto.Title ?? null,
      date: dto.Date ?? null,
      typeLabel: dto.TypeLabel ?? null,
      patientId: dto.PatientId ?? null,
    };
  }

  public toConsultationOverview(dto: ClientConsultationOverviewDto): ClientConsultationOverview {
    return {
      id: dto.ExternalId,
      complaint: dto.Complaint ?? null,
      statusCode: dto.Status ?? null,
      statusLabel: dto.StatusLabel ?? null,
      typeLabel: dto.TypeLabel ?? null,
      admittedTime: dto.AdmittedTime ?? null,
      started: dto.Started ?? null,
      finished: dto.Finished ?? null,
      ward: dto.Ward ?? null,
      patientLocation: dto.PatientLocation ?? null,
      triage: dto.Triage ?? null,
      veterinarianName: dto.VeterinarianName ?? null,
      departmentName: dto.DepartmentName ?? null,
      invoice: dto.InvoiceId ? {
        id: dto.InvoiceId,
        total: dto.InvoiceTotal ?? null,
        isPending: dto.InvoiceIsPending ?? false,
      } : null,
      notesCount: dto.NotesCount ?? null,
      diagnosesCount: dto.DiagnosesCount,
      dischargeCount: dto.DischargeCount ?? null,
      hasVitalsSnapshot: dto.HasVitalsSnapshot,
      hasConsultationItems: dto.HasConsultationItems,
    };
  }

  public toConsultationDischarge(dto: ClientConsultationDischargeDto): ClientConsultationDischarge {
    return {
      text: dto.Text ?? null,
      printedAt: dto.PrintedAt ?? null,
    };
  }

  public toConsultationDetailTreatment(dto: ClientTreatmentDto): ClientConsultationDetailTreatment {
    return {
      id: dto.ExternalId,
      consultationId: dto.ConsultationId ?? null,
      name: dto.Name ?? null,
      instructions: dto.Instructions ?? null,
      used: dto.Used ?? null,
      quantity: dto.Quantity ?? null,
      unit: dto.Unit ?? null,
      veterinarianName: dto.SupervisingVeterinarianName ?? null,
      administrationMethod: dto.AdministrationMethod ?? null,
      typeCode: dto.TypeCode ?? null,
      typeLabel: dto.TypeLabel ?? null,
    };
  }

  public toConsultationDetailSupply(dto: ClientConsultationSupplyDto): ClientConsultationDetailSupply {
    return {
      id: dto.ExternalId,
      consultationId: dto.ConsultationId ?? null,
      patientId: dto.PatientId ?? null,
      name: dto.Name ?? null,
      instructions: dto.Instructions ?? null,
      used: dto.Used ?? null,
      quantity: dto.Quantity ?? null,
      unit: dto.Unit ?? null,
      packageDescription: dto.PackageDescription ?? null,
      usageSize: dto.UsageSize ?? null,
      supervisingVeterinarianId: dto.SupervisingVeterinarianId ?? null,
      supervisingVeterinarianName: dto.SupervisingVeterinarianName ?? null,
      performedById: dto.PerformedById ?? null,
      performedByName: dto.PerformedByName ?? null,
    };
  }

  public toConsultationDetailLaboratoryAnalysis(
    dto: ClientConsultationLaboratoryAnalysisDto,
  ): ClientConsultationDetailLaboratoryAnalysis {
    return {
      id: dto.ExternalId,
      consultationId: dto.ConsultationId ?? null,
      patientId: dto.PatientId ?? null,
      name: dto.Name ?? null,
      code: dto.Code ?? null,
      used: dto.Used ?? null,
      result: dto.Result ?? null,
      resultNumeric: dto.ResultNumeric ?? null,
      resultEntered: dto.ResultEntered ?? null,
      referenceLow: dto.ReferenceLow ?? null,
      referenceHigh: dto.ReferenceHigh ?? null,
      measurementUnit: dto.MeasurementUnit ?? null,
      remarks: dto.Remarks ?? null,
      approved: dto.Approved ?? null,
      approvedDate: dto.ApprovedDate ?? null,
      supervisingVeterinarianId: dto.SupervisingVeterinarianId ?? null,
      supervisingVeterinarianName: dto.SupervisingVeterinarianName ?? null,
      performedById: dto.PerformedById ?? null,
      performedByName: dto.PerformedByName ?? null,
      referralId: dto.ReferralId ?? null,
      device: dto.Device ?? null,
    };
  }

  // Same per-item mapping as the `attachments` section entry in `toConsultationSection` below
  // (both back onto the module-level `toAttachmentFile`), exposed as its own method for the
  // lab-request drawer's standalone attachment-lookup endpoint -- mirrors patients'
  // `ClinicalHistoryMapper.toConsultationAttachmentFiles`.
  public toConsultationAttachmentFiles(
    dtos: readonly ClientConsultationAttachmentDto[],
  ): readonly ClientConsultationAttachmentFile[] {
    return dtos.map(dto => toAttachmentFile(dto));
  }

  public toConsultationDetailTreatmentPlan(dto: ClientTreatmentPlanDto): ClientConsultationDetailTreatmentPlan {
    return {
      id: dto.ExternalId,
      consultationId: dto.ConsultationId,
      startDate: dto.StartDate ?? null,
      endDate: dto.EndDate ?? null,
      ongoing: dto.Ongoing ?? null,
      timings: dto.Timings ?? null,
      everyNDays: dto.EveryNDays ?? null,
      repetitionsPerDay: dto.RepetitionsPerDay ?? null,
      isCabinetItem: dto.IsCabinetItem ?? null,
      doseCount: dto.DoseCount ?? null,
      itemKind: isTreatmentPlanItemKind(dto.ItemKind) ? dto.ItemKind : null,
      itemName: dto.ItemName ?? null,
      itemCode: dto.ItemCode ?? null,
      quantity: dto.Quantity ?? null,
      unit: dto.Unit ?? null,
      packageDescription: dto.PackageDescription ?? null,
      usageSize: dto.UsageSize ?? null,
      instructions: dto.Instructions ?? null,
    };
  }

  // One entry point for all 13 sections, twin of patients' `toConsultationSection` -- the caller
  // passes the same literal the URL carries and gets that section's mapped payload back.
  public toConsultationSection<K extends ClientConsultationSectionKey>(
    section: K, dto: ClientConsultationSectionDtoMap[K],
  ): ClientConsultationSectionDataMap[K] {
    const mappers: ClientConsultationSectionMappers = {
      overview: value => this.toConsultationOverview(value),
      vitals: value => (value ? this.toVitalsSnapshot(value) : null),
      diagnoses: value => value.map(item => this.toConsultationDetailDiagnosis(item)),
      medicines: value => value.map(item => this.toConsultationDetailMedicine(item)),
      procedures: value => value.map(item => this.toConsultationDetailTreatment(item)),
      supplies: value => value.map(item => this.toConsultationDetailSupply(item)),
      analysis: value => value.map(item => this.toConsultationDetailLaboratoryAnalysis(item)),
      plans: value => value.map(item => this.toConsultationDetailTreatmentPlan(item)),
      prescriptions: value => value.map(item => this.toPrescription(item)),
      notes: value => value.map(item => toNoteEntry(item)),
      discharge: value => this.toConsultationDischarge(value),
      attachments: value => value.map(item => toAttachmentFile(item)),
    };

    return mappers[section](dto);
  }

  public toPrescription(dto: ClientPrescriptionDto): ClientPrescription {
    return {
      id: dto.ExternalId,
      consultationId: dto.ConsultationId ?? null,
      patientId: dto.PatientId ?? null,
      patientName: null,
      prescriberName: dto.PrescriberName ?? null,
      issueTime: dto.IssueTime ?? null,
      validMonths: dto.ValidMonths ?? null,
      validDays: dto.ValidDays ?? null,
      controlled: dto.Controlled ?? null,
      microbial: dto.Microbial ?? null,
      diagnosis: dto.Diagnosis ?? null,
      items: dto.Items.map(item => this.toPrescriptionItem(item)),
      documentId: dto.DocumentId ?? null,
      sentAtUtc: dto.SentAtUtc ?? null,
    };
  }

  private toPrescriptionItem(dto: ClientPrescriptionItemDto): ClientPrescriptionItem {
    return {
      id: dto.ExternalId,
      code: dto.Code ?? null,
      name: dto.Name ?? null,
      quantity: dto.Quantity ?? null,
      instructions: dto.Instructions ?? null,
      units: dto.Units ?? null,
      unit: dto.Unit ?? null,
      packageDescription: dto.PackageDescription ?? null,
      usageSize: dto.UsageSize ?? null,
      dosageUnits: dto.DosageUnits ?? null,
      withdrawalPeriods: dto.WithdrawalPeriods ?? null,
      refills: dto.Refills ?? null,
      dosageForm: dto.DosageForm ?? null,
    };
  }

  public toPatientPrescriptions(dto: ClientPatientPrescriptionsDto): ClientPatientPrescriptions {
    return {
      patientId: dto.PatientId,
      patientName: dto.PatientName,
      prescriptions: dto.Prescriptions.map(item => ({ ...this.toPrescription(item), patientId: dto.PatientId, patientName: dto.PatientName })),
    };
  }

  // The four rehydrators below rebuild the record shape the timeline cards render from the flat
  // per-type fields the entry carries -- there is no separate per-record fetch any more. An entry
  // the backend could not attribute to a pet falls back to an empty patient tag rather than being
  // dropped: the card still renders its title/date, and the tag is display-only here.
  private toEntryDiagnosis(dto: ClientClinicalTimelineEntryDto): ClientDiagnosis {
    return {
      id: dto.SourceId,
      patientId: dto.PatientId ?? '',
      patientName: dto.PatientName ?? '',
      consultationId: dto.ConsultationId ?? null,
      code: dto.DiagnosisCode ?? null,
      name: dto.Title,
      date: dto.Date ?? null,
      description: dto.Description ?? null,
      category: dto.DiagnosisCategory ?? null,
      type: dto.DiagnosisType ?? null,
      // Resolving a diagnosis's supervising veterinarian needs an extra ProVet round trip the
      // clinical-timeline route deliberately does not make (see the backend DTO's doc-comment).
      veterinarianName: null,
    };
  }

  private toEntryVaccination(dto: ClientClinicalTimelineEntryDto): ClientVaccination {
    return {
      id: dto.SourceId,
      patientId: dto.PatientId ?? '',
      patientName: dto.PatientName ?? '',
      name: dto.Title,
      diseases: dto.VaccinationDiseases ?? [],
      givenDate: dto.VaccinationGivenDate ?? null,
      expiryDate: dto.VaccinationExpiryDate ?? null,
    };
  }

  // `Date`/`Description` are the treatment's own `Used`/`Instructions` -- see how the backend
  // composes a Treatment entry in `PatientClinicalHistoryQueryService`.
  private toEntryTreatment(dto: ClientClinicalTimelineEntryDto): ClientTreatment {
    return {
      id: dto.SourceId,
      patientId: dto.PatientId ?? '',
      patientName: dto.PatientName ?? '',
      consultationId: dto.ConsultationId ?? null,
      name: dto.Title,
      instructions: dto.Description ?? null,
      used: dto.Date ?? null,
      quantity: dto.TreatmentQuantity ?? null,
      unit: dto.TreatmentUnit ?? null,
      veterinarianName: dto.VeterinarianName ?? null,
      administrationMethod: dto.TreatmentAdministrationMethod ?? null,
      typeCode: dto.TreatmentTypeCode ?? null,
      typeLabel: dto.TreatmentTypeLabel ?? null,
    };
  }

  private toEntryDeworming(dto: ClientClinicalTimelineEntryDto): ClientDeworming {
    return {
      id: dto.SourceId,
      patientId: dto.PatientId ?? '',
      patientName: dto.PatientName ?? '',
      name: dto.Title,
      description: dto.Description ?? null,
      date: dto.Date ?? null,
    };
  }

  public toConsultationDetailDiagnosis(dto: ClientDiagnosisDto): ClientConsultationDetailDiagnosis {
    return {
      id: dto.ExternalId,
      consultationId: dto.ConsultationId ?? null,
      code: dto.Code ?? null,
      name: dto.Name ?? null,
      date: dto.Date ?? null,
      description: dto.Description ?? null,
      category: dto.Category ?? null,
      type: dto.Type ?? null,
      veterinarianName: dto.SupervisingVeterinarianName ?? null,
    };
  }

  public toConsultationDetailMedicine(dto: ClientConsultationMedicineDto): ClientConsultationDetailMedicine {
    return {
      id: dto.ExternalId,
      consultationId: dto.ConsultationId ?? null,
      patientId: dto.PatientId ?? null,
      vaccination: dto.Vaccination,
      name: dto.Name ?? null,
      used: dto.Used ?? null,
      quantity: dto.Quantity ?? null,
      unit: dto.Unit ?? null,
      administrationMethodCode: dto.AdministrationMethodCode ?? null,
      administrationMethodLabel: dto.AdministrationMethodLabel ?? null,
      vaccinationTypeLabel: dto.VaccinationTypeLabel ?? null,
      vaccinationTypeCode: dto.VaccinationTypeCode ?? null,
      activeSubstance: dto.ActiveSubstance ?? null,
      manufacturerData: dto.ManufacturerData ?? null,
      preparation: dto.Preparation ?? null,
      contraindications: dto.Contraindications ?? null,
      instructions: dto.Instructions ?? null,
      withdrawalPeriods: dto.WithdrawalPeriods ?? null,
      veterinarianId: dto.SupervisingVeterinarianId ?? null,
      veterinarianName: dto.SupervisingVeterinarianName ?? null,
      medicationDiagnosisId: dto.MedicationDiagnosisId ?? null,
      invoiceRowId: dto.InvoiceRowId ?? null,
      performedById: dto.PerformedById ?? null,
      performedByName: dto.PerformedByName ?? null,
      usageTypeCode: dto.UsageTypeCode ?? null,
      usageTypeLabel: dto.UsageTypeLabel ?? null,
      administrationHours: dto.AdministrationHours ?? null,
      administrationSpeed: dto.AdministrationSpeed ?? null,
      medicationDurationDays: dto.MedicationDurationDays ?? null,
      controlledSubstance: dto.ControlledSubstance ?? null,
    };
  }
}
